/**
 * Cobertura dos dias na barreira final.
 *
 * A barreira (`validateSchedule`) só sabe cortar dentro de um dia: uma paragem
 * que não cabe no Dia 1 (chegada tardia) ou no último dia (bloco de partida)
 * desaparecia da rota inteira e o dia ficava vazio na interface.
 *
 * Este módulo mantém a mesma barreira, mas RE-OFERECE cada paragem removida aos
 * restantes dias antes de a deixar cair, e reporta os dias que ficaram sem
 * nenhuma paragem — a interface precisa de saber que o dia existe.
 *
 * Módulo puro: sem IO, sem React, sem relógio.
 */

import type { DayWindow } from "./day-windows";
import {
  validateSchedule,
  type ScheduleValidationTrace,
  type ValidatableStop,
} from "./schedule-validation";

export type CoverageInput = {
  stops: ValidatableStop[];
  dayWindows: DayWindow[];
  defaultVisitMinutes?: number;
  reservedMinutesByDay?: Record<number, number>;
};

export type CoverageResult = {
  /** Paragens finais, já validadas, com dia possivelmente reatribuído. */
  stops: ValidatableStop[];
  trace: ScheduleValidationTrace & {
    /** Paragens salvas por reatribuição a outro dia. */
    reassignedStops: number;
    /** Dias sem qualquer paragem depois da barreira. */
    emptyDays: number[];
  };
};

const dayNumbers = (windows: DayWindow[], stops: ValidatableStop[]): number[] => {
  const days = new Set<number>();
  for (const w of windows) days.add(w.day);
  for (const s of stops) days.add(s.day);
  return [...days].sort((a, b) => a - b);
};

/**
 * Corre a barreira final e recupera as paragens removidas oferecendo-as aos
 * outros dias. Uma paragem só cai quando NENHUM dia a consegue acomodar.
 */
export function validateScheduleWithDayCoverage(input: CoverageInput): CoverageResult {
  const first = validateSchedule(input);
  const days = dayNumbers(input.dayWindows, input.stops);
  const keptIds = new Set(first.stops.map((s) => s.id));
  const dropped = input.stops.filter((s) => !keptIds.has(s.id));

  let current = first;
  let reassignedStops = 0;

  for (const stop of dropped) {
    // Locais fixos/pedidos explicitamente nunca são movidos em silêncio para
    // outro dia: só paragens opcionais podem ser realocadas.
    if (stop.required) continue;
    for (const day of days) {
      if (day === stop.day) continue;
      const attempt = validateSchedule({
        ...input,
        stops: [...current.stops, { ...stop, day }],
      });
      if (attempt.stops.some((s) => s.id === stop.id) && attempt.stops.length > current.stops.length) {
        current = attempt;
        reassignedStops += 1;
        break;
      }
    }
  }


  const usedDays = new Set(current.stops.map((s) => s.day));
  const emptyDays = days.filter((d) => !usedDays.has(d));

  return {
    stops: current.stops,
    trace: {
      ...current.trace,
      inputStops: first.trace.inputStops,
      droppedStops: Math.max(0, first.trace.inputStops - current.stops.length),
      reassignedStops,
      emptyDays,
    },
  };
}

/**
 * Ordem final para apresentação: por dia e, dentro do dia, pela ordem
 * devolvida pela barreira. Garante que nenhum dia com paragens é omitido.
 */
export function orderByDay<T extends { day: number }>(stops: readonly T[]): T[] {
  return stops
    .map((s, index) => ({ s, index }))
    .sort((a, b) => (a.s.day - b.s.day) || (a.index - b.index))
    .map(({ s }) => s);
}
